import React, { FC, useEffect, useState } from 'react'
import { CheckIcon } from '@heroicons/react/outline'
import { ArrowRightIcon, DownloadIcon } from '@heroicons/react/solid'
import RenderSlate from '../Slate/RenderSlate'
import {
  CourseType, LessonType, ModuleType, FileObjectType, LessonResourceType
} from '../../types'
import { VideoPlayer, Button, Heading } from '../System'
import { StyledLessonContent } from '.'
import useSegment from '../utils/useSegment'

type Props = {
  currCourse: CourseType;
  currModule: ModuleType;
  currLesson: LessonType;
  nextLesson?: LessonType;
  toggleLessonComplete(lesson: LessonType, moveToNext?: boolean): void;
}

const LessonContent: FC<Props> = ({
  currCourse,
  currModule,
  currLesson,
  nextLesson,
  toggleLessonComplete
}) => {
  const COUNTDOWN_SECONDS = 5

  const { track } = useSegment()
  const [upNextTimer, setUpNextTimer] = useState<NodeJS.Timeout>()
  const [upNextCountdown, setUpNextCountdown] = useState(COUNTDOWN_SECONDS)

  const handleUpNextTimer = (action: 'start' | 'stop' = 'start') => {
    if (!nextLesson) {
      return
    }

    setUpNextCountdown(COUNTDOWN_SECONDS)

    if (upNextTimer) {
      clearInterval(upNextTimer)
      setUpNextTimer(undefined)
    }

    if (action === 'start') {
      const timer = setInterval(() => {
        setUpNextCountdown((time) => time - 1)
      }, 1000)

      setUpNextTimer(timer)
    }
  }

  // Track when the user starts the lesson's video
  const trackVideoStart = () => {
    track('Viewed Lesson Video', {
      id: currLesson.video?.svid,
      course_title: currCourse.title,
      course_id: currCourse._id,
      module_title: currModule.title,
      module_id: currModule._id,
      lesson_title: currLesson.title,
      lesson_id: currLesson._id
    })
  }

  // Track when the user downloads a resource
  const trackDownloadResource = (resource?: LessonResourceType) => {
    track('Downloaded Lesson Resource', {
      label: resource?.label,
      url: resource?.file?.fileHttpLink,
      course_title: currCourse.title,
      course_id: currCourse._id,
      module_title: currModule.title,
      module_id: currModule._id,
      lesson_title: currLesson.title,
      lesson_id: currLesson._id
    })
  }

  useEffect(() => {
    if (upNextCountdown === 0) {
      setUpNextCountdown(COUNTDOWN_SECONDS)
      setUpNextTimer(undefined)
      toggleLessonComplete(currLesson, true)
    }
  }, [upNextCountdown])

  useEffect(() => {
    return () => {
      if (upNextTimer) {
        clearInterval(upNextTimer)
      }
    }
  }, [])

  return (
    <StyledLessonContent>
      <div className="lesson-video">
        {currLesson.video && currLesson.video.svid && (
          <VideoPlayer
            videoId={currLesson.video?.svid}
            onStart={() => trackVideoStart()}
            onEnd={() => handleUpNextTimer()}
          />
        )}
        {nextLesson && !currLesson.isCompleted && upNextTimer && (
          <div className="lesson-up-next-container">
            <div className="lesson-up-next">
              <span>Up next in {upNextCountdown} seconds</span>
              <div onClick={() => toggleLessonComplete(currLesson, true)} className="thumbnail">
                {nextLesson.thumbnail?.fileHttpLink && (
                  <img src={nextLesson.thumbnail.fileHttpLink} alt="" />
                )}
              </div>
              <span>{nextLesson.title}</span>
              <div className="actions">
                <Button onClick={() => handleUpNextTimer('stop')} type="small" theme="outline">
                  Cancel
                </Button>
                <Button onClick={(e) => toggleLessonComplete(currLesson, true)} type="small">
                  Watch Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="lesson-details">
        <Heading>{currLesson.title}</Heading>
        <div className="lesson-actions">
          <Button
            onClick={() => toggleLessonComplete(currLesson, true)}
            type="small"
            theme={currLesson.isCompleted ? 'outline' : 'solid'}
            iconPos="right"
          >
            {currLesson.isCompleted && (
              <>
                COMPLETED <CheckIcon />
              </>
            )}
            {!currLesson.isCompleted && (
              <>
                COMPLETE <ArrowRightIcon />
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="lesson-content">
        <div className="lesson-description">
          <span className="sub-title">Description</span>
          <div className="description">
            <RenderSlate html={currLesson.description} />
          </div>
        </div>

        <div className="lesson-downloads">
          {currLesson.workbook && currLesson.workbook.fileHttpLink && (
            <Button
              theme="outline"
              externalURL={currLesson.workbook.fileHttpLink}
              type="small"
              iconPos="right"
              target="_blank"
            >
              Workbook
              <DownloadIcon />
            </Button>
          )}
          {currLesson.additionalFiles && currLesson.additionalFiles.length > 0 && (
            <div className="lesson-additional-content">
              <b>More Downloads</b>
              <ul>
                {currLesson.additionalFiles.map((file, index) => {
                  return (
                    <li key={index} onClick={() => trackDownloadResource(file)}>
                      <a href={file.file?.fileHttpLink} target="_blank" rel="noreferrer noopener">
                        {file.label}
                        <DownloadIcon />
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </StyledLessonContent>
  )
}

export default LessonContent
